// Admin audit log: view merged duplicate users — who merged whom, when, and stats.
import { useEffect, useState } from "react";
import api from "@/api/client";
import { X, History, User, RefreshCw, ArrowRight, Wallet, Star } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function MergeLogModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/merge-logs", { params: { limit: 200 } });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر تحميل سجل الدمج");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
         onClick={onClose} data-testid="merge-log-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full sm:max-w-3xl rounded-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center">
              <History size={18}/>
            </div>
            <div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>سجل عمليات الدمج</div>
              <div className="text-xs text-[var(--text-secondary)]">
                إجمالي: <b>{total}</b> عملية {loading ? "..." : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-xl bg-[var(--surface-alt)] hover:bg-blue-50"
                    title="تحديث" data-testid="merge-log-refresh">
              <RefreshCw size={16}/>
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"
                    data-testid="merge-log-close">
              <X size={16}/>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {err && <div className="text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg">{err}</div>}
          {!loading && logs.length === 0 && (
            <div className="text-center py-10 text-[var(--text-secondary)]">
              <History size={40} className="mx-auto mb-2 opacity-30"/>
              <div className="text-sm">لم تُنفَّذ أي عملية دمج بعد.</div>
            </div>
          )}

          {logs.map((log) => (
            <div key={log.id} className="rounded-2xl border border-[var(--border)] bg-white p-4 talabya-shadow-soft"
                 data-testid={`merge-log-${log.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-[var(--text-secondary)]">
                  {fmtDate(log.created_at)}
                </div>
                <div className="text-[10px] font-bold text-primary-brand bg-primary-light px-2 py-0.5 rounded">
                  {log.merged_count} حساب
                </div>
              </div>

              {/* Merge visualization */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3">
                {/* Sources */}
                <div className="space-y-1.5">
                  <div className="text-[10px] font-black text-red-600 mb-1">تم دمجه (حُذف)</div>
                  {(log.sources || []).map((s) => (
                    <div key={s.id} className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2"
                         data-testid={`merge-src-${s.id}`}>
                      <User size={14} className="text-red-500"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold truncate">{s.name || "بدون اسم"}</div>
                        <div className="text-[10px] text-[var(--text-secondary)] truncate" dir="ltr">
                          {s.phone || s.email || s.id}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Arrow */}
                <div className="flex justify-center">
                  <ArrowRight className="text-primary-brand rotate-180 md:rotate-180" size={22}/>
                </div>

                {/* Keeper */}
                <div>
                  <div className="text-[10px] font-black text-emerald-700 mb-1">الحساب المُبقى</div>
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2"
                       data-testid={`merge-keeper-${log.keeper?.id}`}>
                    <User size={14} className="text-emerald-600"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold truncate">{log.keeper?.name || "—"}</div>
                      <div className="text-[10px] text-[var(--text-secondary)] truncate" dir="ltr">
                        {log.keeper?.phone || log.keeper?.email || log.keeper?.id}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              {log.stats && (
                <div className="mt-3 pt-3 border-t border-dashed border-[var(--border)] grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="bg-[var(--surface-alt)] rounded-lg px-2 py-1.5">
                    <div className="text-[10px] text-[var(--text-secondary)]">طلبات مرحّلة</div>
                    <div className="font-black">{log.stats.orders || 0}</div>
                  </div>
                  <div className="bg-[var(--surface-alt)] rounded-lg px-2 py-1.5">
                    <div className="text-[10px] text-[var(--text-secondary)]">حركات محفظة</div>
                    <div className="font-black">{log.stats.wallet_txs || 0}</div>
                  </div>
                  <div className="bg-[var(--surface-alt)] rounded-lg px-2 py-1.5 flex items-center gap-1">
                    <Wallet size={12} className="text-primary-brand"/>
                    <div>
                      <div className="text-[10px] text-[var(--text-secondary)]">رصيد مُضاف</div>
                      <div className="font-black">{Number(log.stats.wallet_added || 0).toFixed(2)} د.ل</div>
                    </div>
                  </div>
                  <div className="bg-[var(--surface-alt)] rounded-lg px-2 py-1.5 flex items-center gap-1">
                    <Star size={12} className="text-amber-500"/>
                    <div>
                      <div className="text-[10px] text-[var(--text-secondary)]">نقاط ولاء</div>
                      <div className="font-black">{log.stats.points_added || 0}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-2 text-[10px] text-[var(--text-secondary)]">
                نُفّذت بواسطة: <b>{log.admin_name || log.admin_id?.slice(0, 8) || "—"}</b>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
